<template>
  <div class="formcell">
    <label v-if="labelText" :for="id" class="label">
      <slot>
        {{ labelText }}
      </slot>
      <span v-if="required" class="required">*</span>
    </label>
    <slot name="input-container">
      <div
        class="formcell-container form-input input w-input"
        :class="{
          'input-error': error,
          'input-success': success,
          'input-autocomplete': isAutocomplete,
          'input-disabled': disabled,
        }"
        @click="setFocusHandler"
      >
        <component :is="icon" class="formcell-input-icon" />
        <slot name="pre" />
        <input
          :id="id"
          :ref="inputRef"
          :value="customValue"
          :type="type"
          :title="title"
          :pattern="pattern"
          :class="{
            'input-with-icon': icon,
          }"
          :autocomplete="autocomplete"
          :required="required"
          :disabled="disabled"
          :step="step"
          :maxlength="maxLength"
          :minlength="minLength"
          :min="min"
          :max="max"
          :placeholder="placeholder"
          @focus="$emit('focus')"
          @input="handleInput"
          @click="handleClick"
        />
        <slot name="post" />
      </div>
    </slot>
    <div v-if="$slots.autocomplete" class="formcell__autocomplete">
      <slot name="autocomplete" />
    </div>
    <div
      v-if="$slots['dropdown-autocomplete']"
      class="formcell__autocomplete formcell__dropdown-autocomplete"
    >
      <slot name="dropdown-autocomplete" />
    </div>
    <span v-if="error" class="error-message">{{ errorMessageText }}</span>
    <span v-else-if="inputInfo" class="info-message">{{ inputInfo }}</span>
    <slot v-else name="info" />
  </div>
</template>

<script>
  import inputErrors from '@/utils/mixins/input-errors';
  import SearchIcon from '@/assets/icons/search.svg';

  const CUSTOM_NUMBER_TYPE = 'custom-number';

  export default {
    name: 'FormInput',
    components: {
      SearchIcon,
    },
    mixins: [inputErrors],
    model: {
      prop: 'value',
      event: 'input',
    },
    props: {
      value: [String, Number],
      id: {
        type: String,
        required: true,
      },
      required: {
        type: Boolean,
        required: false,
        default: false,
      },
      placeholder: {
        type: String,
        required: false,
        default: '',
      },
      labelText: {
        type: String,
      },
      type: {
        type: String,
        default: 'text',
      },
      autocomplete: {
        type: String,
        default: 'off',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      inputInfo: {
        type: String,
        default: '',
      },
      maxLength: Number,
      minLength: Number,
      icon: Object,
      min: String,
      max: String,
      step: String,
    },
    computed: {
      inputRef() {
        return `form-input-${this.id}`;
      },
      isAutocomplete() {
        return !!this.$slots.autocomplete || !!this.$slots['dropdown-autocomplete'];
      },
      customValue() {
        return this.type === CUSTOM_NUMBER_TYPE && this.value
          ? this.value.toLocaleString()
          : this.value;
      },
      getDecimalSeparatorRegExp() {
        const numberWithDecimalSeparator = 1000;
        let symbol = numberWithDecimalSeparator.toLocaleString().substring(1, 2);
        return new RegExp(`\\b${symbol}\\b`, 'g');
      },
      pattern() {
        return this.type === 'tel' ? '\\+[1-9]{1}[0-9]{3,14}' : null;
      },
      title() {
        return this.type === 'tel' ? `+<${this.$i18n.t('country code')}>#######` : null;
      },
    },
    methods: {
      handleInput(event) {
        if (this.type === CUSTOM_NUMBER_TYPE) {
          let cleanedString = +event.target.value.replace(this.getDecimalSeparatorRegExp, '');
          if (!isNaN(cleanedString)) this.$emit('input', cleanedString);
        } else this.$emit('input', event.target.value);
      },
      handleClick(event) {
        if (this.type === 'date') {
          event.preventDefault();
        }
      },
      setFocusHandler() {
        try {
          this.$refs[this.inputRef]?.focus();
          this.$emit('focus');
        } catch (e) {
          console.error(e);
        }
      },
    },
  };
</script>

<style lang="scss">
  @import 'src/styles/components/input';
  .form-input {
    margin-bottom: 4px !important;

    input {
      border-radius: 0;
    }
    input:-webkit-autofill {
      -webkit-box-shadow: inset 0 0 0 50px #fff;
      border: 0;
    }
    input::placeholder {
      color: $text-tertiary;
    }
  }

  .input-disabled {
    background: #e8e8e8 !important;
    color: #8892a0 !important;

    &:hover {
      border-color: #dadde2 !important;
    }
  }

  .formcell-input-icon {
    top: 50%;
    position: absolute;
    transform: translateY(-50%);
  }

  .default-slot {
    top: 50%;
    position: absolute;
    transform: translateY(-50%);
    right: 12px;
  }

  .input-with-icon {
    padding-left: 24px !important;
  }

  .formcell-container {
    position: relative;
    display: flex !important;
    flex-direction: row;
    align-items: center;
  }

  .input-autocomplete {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .formcell__autocomplete {
    margin-top: -5px;
    border: 1px solid #dadde2;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    width: 100%;
    background: white;
    z-index: 2;
  }

  .formcell__autocomplete:not(.formcell__dropdown-autocomplete) {
    position: absolute;
  }

  .required {
    color: $semantic-error;
  }

  input[type='date']::-webkit-input-placeholder {
    visibility: hidden !important;
  }

  input::-webkit-calendar-picker-indicator,
  input::-webkit-inner-spin-button {
    display: none !important;
  }

  input[type='date']::-webkit-inner-spin-button,
  input[type='date']::-webkit-outer-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }
</style>
